import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  debounce,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';
import PageBody from '~/components/Page/PageBody';
import PageHeaderBar from '~/components/Page/PageHeaderBar';
import PageLayout from '~/components/Page/PageLayout';
import PageSidebar from '~/components/Page/PageSidebar';

import dealPageService from '~/services/cruises/DealPageService';

import CruisesSidebar from '../../CruisesSidebar/CruisesSidebar';
import useCruiseDealsPage from '../../context/useCruiseDealsPage';

import DealPageListOrderItem from './components/DealPageListOrderItem';
import { CruiseDealPageBase } from './types';

export default function DealPageOrdering() {
  const { dealsPage, isLoading: isFetchingDeals } = useCruiseDealsPage({ recordsPerPage: 100 });
  const [orderedDealsPage, setOrderedDealsPage] = useState<Array<CruiseDealPageBase>>(dealsPage || []);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Update the orderedDealsPage when the dealsPage changes
  useEffect(() => {
    setOrderedDealsPage(dealsPage);
  }, [dealsPage]);

  const debouncedHandleDragSort = useMemo(
    () =>
      debounce(async (dealPageIds: Array<string>) => {
        await dealPageService.orderingDealsPage(dealPageIds);
        enqueueSnackbar('Deals pages ordered with success', { variant: 'success' });
        setIsLoading(false);
      }, 500),
    [enqueueSnackbar],
  );

  const handleDragSort = useCallback(
    async ({ active, over }) => {
      if (active.id !== over.id) {
        try {
          setIsLoading(true);
          const oldIndex = orderedDealsPage.map((e) => e.id).indexOf(active.id);
          const newIndex = orderedDealsPage.map((e) => e.id).indexOf(over.id);
          const newList = arrayMove(orderedDealsPage, oldIndex, newIndex).map((dealPage, index) => ({
            ...dealPage,
            order: index + 1,
          }));

          setOrderedDealsPage(newList);

          const dealPageIds = newList.map((e) => e.id);
          debouncedHandleDragSort(dealPageIds);
        } catch (error) {
          setIsLoading(false);
          enqueueSnackbar('Error ordering deals pages', { variant: 'error' });
        }
      }
    },
    [debouncedHandleDragSort, enqueueSnackbar, orderedDealsPage],
  );

  return (
    <PageLayout hasLeftSidebar hasRightSidebar>
      <PageHeaderBar title="Deals Pages Ordering" />
      <PageSidebar placement="start" width="medium">
        <CruisesSidebar />
      </PageSidebar>
      <PageBody>
        {isFetchingDeals ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Spinner size={40} />
          </Box>
        ) : (
          <Box>
            <DndContext onDragEnd={handleDragSort}>
              <TableContainer component={Paper}>
                <Table aria-label="simple table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1}>
                          Deal Page Name
                          {!!isLoading && <Spinner color="white" size={15} inline />}
                        </Stack>
                      </TableCell>
                      <TableCell>Cruise Line</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <SortableContext items={orderedDealsPage || []}>
                      {orderedDealsPage.map((dealPage) => (
                        <DealPageListOrderItem key={dealPage.id} dealPage={dealPage} />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </TableContainer>
            </DndContext>
          </Box>
        )}
      </PageBody>
    </PageLayout>
  );
}
